// KRAYA_POS_QRIS_GENERIC_UI_V2
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Minus, X, ShoppingCart, Banknote, QrCode, CheckCircle2, Printer, Trash2, RefreshCw, Bluetooth, Unplug, TestTube2, Barcode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  connectReceiptPrinter,
  disconnectReceiptPrinter,
  getReceiptPrinterStatus,
  isWebSerialSupported,
  loadPrinterSettings,
  printEscPosReceipt,
  printEscPosTest,
  savePrinterSettings,
} from "@/lib/receiptPrinter";

const receiptNumber = (value) => Math.round(Number(value || 0)).toLocaleString("id-ID");

const POS_VIEW_MODE_KEY = "kraya.pos.viewMode"; // legacy key, retained for migration
const POS_DEFAULT_VIEW_MODE_KEY = "kraya.pos.defaultViewMode";

const normalizePosViewMode = (value) => value === "market" ? "market" : "touch";

const loadPosDefaultViewMode = () => {
  if (typeof window === "undefined") return "touch";
  const savedDefault = window.localStorage.getItem(POS_DEFAULT_VIEW_MODE_KEY);
  if (savedDefault === "market" || savedDefault === "touch") return savedDefault;
  return normalizePosViewMode(window.localStorage.getItem(POS_VIEW_MODE_KEY));
};

const parsePromoDate = (value, endOfDay = false) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? `${raw}T${endOfDay ? "23:59:59.999" : "00:00:00"}`
    : raw;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isPromoCurrentlyActive = (promo, now = new Date()) => {
  if (!promo || promo.active === false) return false;
  const startsAt = parsePromoDate(promo.start_date, false);
  const endsAt = parsePromoDate(promo.end_date, true);
  if (startsAt && now < startsAt) return false;
  if (endsAt && now > endsAt) return false;
  return true;
};

const parseQrisAdditionalInfo = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getQrisReceiptDetails = (sale) => {
  const qris = sale?.qris || {};
  const paidInfo = parseQrisAdditionalInfo(qris.paid_inquiry?.additionalInfo);
  const lastInfo = parseQrisAdditionalInfo(qris.last_inquiry?.additionalInfo);
  return {
    customerName: String(sale?.customer_name || paidInfo.customerName || lastInfo.customerName || "").trim(),
    approvalCode: String(qris.approval_code || paidInfo.approvalCode || lastInfo.approvalCode || "").trim(),
  };
};

export default function POS() {
  const { hasFeature } = useAuth();
  const canUsePromos = hasFeature("promos");
  const canUseTax = hasFeature("tax_ppn");
  const canUseQris = hasFeature("qris");

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);
  const [promoChecking, setPromoChecking] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState("11");
  const [method, setMethod] = useState("cash");
  const [cash, setCash] = useState("");
  const [paying, setPaying] = useState(false);
  const [qrisSale, setQrisSale] = useState(null);
  const [qrisMode, setQrisMode] = useState({ configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" });
  const [qrisModeLoading, setQrisModeLoading] = useState(false);
  const [staticQrImageUrl, setStaticQrImageUrl] = useState("");
  const [staticQrImageLoading, setStaticQrImageLoading] = useState(false);
  const [paidSale, setPaidSale] = useState(null);
  // Yokke inquiry polling state
  const [inquiryAttempts, setInquiryAttempts] = useState(0);
  const [inquiryStatus, setInquiryStatus] = useState(""); // "polling" | "expired" | "terminal" | ""
  const [qrisRemainingSeconds, setQrisRemainingSeconds] = useState(300);
  const [manualChecking, setManualChecking] = useState(false);
  const receiptRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const autoPrintedSaleRef = useRef("");
  const [printerSettingsOpen, setPrinterSettingsOpen] = useState(false);
  const [printerSettings, setPrinterSettings] = useState(() => loadPrinterSettings());
  const [printerConnected, setPrinterConnected] = useState(() => getReceiptPrinterStatus().connected);
  const [printerLabel, setPrinterLabel] = useState(() => getReceiptPrinterStatus().label);
  const [printerBusy, setPrinterBusy] = useState(false);
  const [defaultViewMode, setDefaultViewModeState] = useState(() => loadPosDefaultViewMode());
  const [viewMode, setViewModeState] = useState(() => loadPosDefaultViewMode());
  const [barcodeValue, setBarcodeValue] = useState("");
  const [marketSearch, setMarketSearch] = useState("");
  const [lastScannedProduct, setLastScannedProduct] = useState(null);
  const barcodeInputRef = useRef(null);
  const marketSearchRef = useRef(null);
  const promoSelectRef = useRef(null);
  const cashInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [o, c] = await Promise.all([api.get("/outlets"), api.get("/categories")]);
      setOutlets(o.data || []);
      setCategories(c.data || []);
      if (o.data?.length) setOutletId(o.data[0].id);
    })();

    if (canUsePromos) {
      api.get("/promos")
        .then((r) => setPromos(r.data || []))
        .catch(() => {
          setPromos([]);
          toast.error("Daftar promo tidak dapat dimuat");
        })
        .finally(() => setPromosLoading(false));
    } else {
      setPromos([]);
      setPromosLoading(false);
    }
  }, [canUsePromos]);

  useEffect(() => {
    if (!canUsePromos) {
      setPromoCode("");
      setAppliedPromo(null);
    }
    if (!canUseTax) setTaxEnabled(false);
    if (!canUseQris && method === "qris") setMethod("cash");
  }, [canUsePromos, canUseTax, canUseQris, method]);

  const loadProducts = useCallback(async () => {
    if (!outletId) return;
    const response = await api.get("/products", { params: { outlet_id: outletId } });
    setProducts(response.data || []);
  }, [outletId]);

  useEffect(() => {
    if (!outletId) return undefined;
    setPromoCode("");
    setAppliedPromo(null);
    loadProducts().catch(() => toast.error("Data stok produk tidak dapat dimuat"));
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") loadProducts().catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, [outletId, loadProducts]);

  useEffect(() => {
    if (!canUseQris || !outletId) {
      setQrisMode({ configured: false, provider: null, mode: null, label: "QRIS tidak tersedia" });
      setMethod((current) => current === "qris" ? "cash" : current);
      return;
    }

    let active = true;
    setQrisModeLoading(true);
    api.get(`/qris-config/outlet/${outletId}/mode`)
      .then((response) => {
        if (!active) return;
        const next = response.data || { configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" };
        setQrisMode(next);
        if (!next.configured) setMethod((current) => current === "qris" ? "cash" : current);
      })
      .catch(() => {
        if (!active) return;
        setQrisMode({ configured: false, provider: null, mode: null, label: "QRIS belum dikonfigurasi" });
        setMethod((current) => current === "qris" ? "cash" : current);
      })
      .finally(() => { if (active) setQrisModeLoading(false); });

    return () => { active = false; };
  }, [outletId, canUseQris]);

  const activePromos = useMemo(() => {
    if (!canUsePromos || !outletId) return [];
    return promos
      .filter((promo) => {
        if (!isPromoCurrentlyActive(promo)) return false;
        const outletIds = Array.isArray(promo.outlet_ids) ? promo.outlet_ids : [];
        return outletIds.length === 0 || outletIds.includes(outletId);
      })
      .sort((a, b) => String(a.name || a.code).localeCompare(String(b.name || b.code), "id"));
  }, [promos, outletId, canUsePromos]);

  const filtered = useMemo(() => {
    let arr = products.filter((p) => p.active !== false);
    if (cat !== "all") arr = arr.filter((p) => p.category_id === cat);
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter((p) => p.name?.toLowerCase().includes(s) || p.sku?.toLowerCase().includes(s) || p.barcode?.toLowerCase().includes(s));
    }
    return arr;
  }, [products, cat, q]);

  const marketSearchResults = useMemo(() => {
    const term = String(marketSearch || "").trim().toLowerCase();
    if (!term) return [];
    return products
      .filter((p) => p.active !== false)
      .filter((p) =>
        String(p.name || "").toLowerCase().includes(term)
        || String(p.sku || "").toLowerCase().includes(term)
        || String(p.barcode || "").toLowerCase().includes(term)
      )
      .slice(0, 10);
  }, [products, marketSearch]);

  const cartProductMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const cartItemCount = useMemo(() => cart.reduce((sum, item) => sum + Number(item.qty || 0), 0), [cart]);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const discount = appliedPromo?.discount || 0;
  const taxableBase = Math.max(0, subtotal - discount);
  const normalizedTaxRate = Math.min(100, Math.max(0, Number(taxRate || 0)));
  const tax = canUseTax && taxEnabled ? Math.round(taxableBase * normalizedTaxRate / 100) : 0;
  const total = Math.max(0, taxableBase + tax);
  const change = method === "cash" ? Math.max(0, Number(cash || 0) - total) : 0;

    const productStock = (productId) => {
      const product = products.find((row) => row.id === productId);
      return product && !Number(product.return_pending_count || 0) ? Math.max(0, Number(product.stock || 0)) : 0;
    };

    // KRAYA_POS_STOCK_GUARD_V1
    const add = (p) => {
      if (Number(p?.return_pending_count || 0) > 0) { toast.error("Produk dikarantina untuk retur dan tidak dapat dijual"); return; }
      const available = Math.max(0, Number(p?.stock || 0));

      if (available <= 0) {
        toast.error(`Stok ${p?.name || "produk"} sudah habis`);
        return;
      }

      setCart((current) => {
        const index = current.findIndex((item) => item.product_id === p.id);
        const currentQty = index >= 0 ? Number(current[index].qty || 0) : 0;

        if (currentQty >= available) {
          toast.error(`Stok ${p.name} hanya tersedia ${available}`);
          return current;
        }

        if (index >= 0) {
          return current.map((item, itemIndex) =>
            itemIndex === index ? { ...item, qty: currentQty + 1 } : item
          );
        }

        return [
          ...current,
          {
            product_id: p.id,
            name: p.name,
            qty: 1,
            price: p.price,
            cost: p.cost || 0,
          },
        ];
      });
    };

  const focusBarcodeInput = () => {
    window.setTimeout(() => barcodeInputRef.current?.focus(), 0);
  };

  const changeViewMode = (mode) => {
    const next = normalizePosViewMode(mode);
    setViewModeState(next);
    setQ("");
    setMarketSearch("");
    setCat("all");
    if (next === "market") focusBarcodeInput();
  };

  const changeDefaultViewMode = (mode) => {
    const next = normalizePosViewMode(mode);
    setDefaultViewModeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(POS_DEFAULT_VIEW_MODE_KEY, next);
    }
    toast.success(`Default Kasir POS: ${next === "market" ? "Minimarket" : "Touch POS"}`);
  };

  const findProductByBarcode = (rawCode) => {
    const normalized = String(rawCode || "").trim().toLowerCase();
    if (!normalized) return null;
    return products.find((p) =>
      p.active !== false
      && (
        String(p.barcode || "").trim().toLowerCase() === normalized
        || String(p.sku || "").trim().toLowerCase() === normalized
      )
    ) || null;
  };

  const scanBarcode = (rawCode = barcodeValue) => {
    const code = String(rawCode || "").trim();
    if (!code) {
      focusBarcodeInput();
      return;
    }

    const product = findProductByBarcode(code);

    setBarcodeValue("");
    if (!product) {
      setMarketSearch(code);
      setLastScannedProduct(null);
      toast.error(`Barcode ${code} tidak ditemukan`);
      focusBarcodeInput();
      return;
    }

    add(product);
    setLastScannedProduct(product);
    setMarketSearch("");
    focusBarcodeInput();
  };

  const addFromMarketSearch = (product) => {
    add(product);
    setLastScannedProduct(product);
    setMarketSearch("");
    focusBarcodeInput();
  };

  useEffect(() => {
    if (viewMode !== "market") return undefined;
    const code = String(barcodeValue || "").trim();
    if (!code) return undefined;

    // Barcode scanners usually type the full code in a very short burst.
    // If the scanner does not send Enter, exact-match the completed input
    // after a short idle window and add it automatically.
    const timer = window.setTimeout(() => {
      if (findProductByBarcode(code)) scanBarcode(code);
    }, 120);
    return () => window.clearTimeout(timer);
    // Barcode lookup intentionally follows the current product snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeValue, viewMode, products]);

    const inc = (i) => {
      setCart((current) => {
        const target = current[i];
        if (!target) return current;

        const available = productStock(target.product_id);
        const currentQty = Number(target.qty || 0);

        if (available <= 0) {
          toast.error(`Stok ${target.name} sudah habis`);
          return current;
        }

        if (currentQty >= available) {
          toast.error(`Stok ${target.name} hanya tersedia ${available}`);
          return current;
        }

        return current.map((item, index) =>
          index === i ? { ...item, qty: currentQty + 1 } : item
        );
      });
    };
  const dec = (i) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, x.qty - 1) } : x));
  const remove = (i) => setCart((c) => c.filter((_, idx) => idx !== i));
  const clear = () => {
    setCart([]);
    setPromoCode("");
    setAppliedPromo(null);
    setTaxEnabled(false);
    setCash("");
    setLastScannedProduct(null);
    setBarcodeValue("");
    setMarketSearch("");
  };

  const applyPromo = async (options = {}) => {
    const { silent = false } = options;
    if (!canUsePromos) {
      setPromoCode("");
      setAppliedPromo(null);
      if (!silent) toast.error("Fitur promo tidak tersedia pada paket saat ini");
      return;
    }
    const selectedCode = String(promoCode || "").trim().toUpperCase();
    if (!selectedCode) {
      setAppliedPromo(null);
      return;
    }
    if (subtotal <= 0) {
      if (!silent) toast.error("Masukkan barang terlebih dahulu");
      setAppliedPromo(null);
      return;
    }

    setPromoChecking(true);
    try {
      const r = await api.post("/promos/validate", {
        code: selectedCode,
        subtotal,
        outlet_id: outletId,
      });
      setAppliedPromo(r.data);
      if (!silent) toast.success(`Promo ${r.data.promo.name} berhasil digunakan`);
    } catch (e) {
      setAppliedPromo(null);
      if (!silent) toast.error(e?.response?.data?.detail || "Promo tidak valid");
    } finally {
      setPromoChecking(false);
    }
  };

  useEffect(() => {
    if (!canUsePromos || !appliedPromo?.promo?.code || !promoCode) return;
    if (subtotal <= 0) {
      setAppliedPromo(null);
      return;
    }

    const timer = window.setTimeout(() => {
      applyPromo({ silent: true });
    }, 250);
    return () => window.clearTimeout(timer);
    // Recalculate the selected promo whenever the cart value or outlet changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, outletId, promoCode, canUsePromos]);

  const pay = async () => {
    if (cart.length === 0) { toast.error("Keranjang kosong"); return; }
    if (!outletId) { toast.error("Pilih outlet"); return; }
    const stockProblem = cart.find((item) => {
      const available = productStock(item.product_id);
      return available <= 0 || Number(item.qty || 0) > available;
    });

    if (stockProblem) {
      const available = productStock(stockProblem.product_id);
      toast.error(
        available <= 0
          ? `Stok ${stockProblem.name} sudah habis`
          : `Stok ${stockProblem.name} hanya tersedia ${available}`
      );

      if (typeof loadProducts === "function") {
        try { await loadProducts(); } catch (_) {}
      }
      return;
    }
    if (method === "qris" && !canUseQris) { toast.error("Fitur QRIS tidak tersedia pada paket saat ini"); return; }
    if (method === "qris" && !qrisMode.configured) { toast.error("QRIS outlet belum dikonfigurasi"); return; }
    if (appliedPromo && !canUsePromos) { toast.error("Fitur promo tidak tersedia pada paket saat ini"); return; }
    if (taxEnabled && !canUseTax) { toast.error("Fitur PPN tidak tersedia pada paket saat ini"); return; }
    if (method === "cash" && Number(cash || 0) < total) { toast.error("Uang tunai kurang"); return; }
    setPaying(true);
    try {
      const items = cart.map((c) => ({ product_id: c.product_id, qty: Number(c.qty || 0) }));
      const body = {
        outlet_id: outletId,
        items,
        discount: canUsePromos ? discount : 0,
        promo_code: canUsePromos ? (appliedPromo?.promo?.code || null) : null,
        tax: canUseTax ? tax : 0,
        tax_rate: canUseTax && taxEnabled ? normalizedTaxRate : 0,
        payment_method: method,
        cash_received: method === "cash" ? Number(cash || 0) : 0,
      };
      const r = await api.post("/sales", body);
      if (method === "qris") {
        setQrisSale(r.data);
      } else {
        setPaidSale({ ...r.data, tax_rate: canUseTax && taxEnabled ? normalizedTaxRate : 0 });
        clear();
        await loadProducts();
        toast.success("Transaksi sukses");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
    } finally { setPaying(false); }
  };

    const incrementLastMarketItem = () => {
      if (cart.length === 0) {
        toast.error("Belum ada barang di transaksi");
        focusBarcodeInput();
        return;
      }

      const preferredProductId = lastScannedProduct?.id;
      const preferredIndex = preferredProductId
        ? cart.findIndex((item) => item.product_id === preferredProductId)
        : -1;
      const targetIndex = preferredIndex >= 0 ? preferredIndex : cart.length - 1;

      inc(targetIndex);
      focusBarcodeInput();
    };

  const runMarketShortcut = (shortcut) => {
    switch (shortcut) {
      case "F2":
        marketSearchRef.current?.focus();
        break;
      case "F3":
        focusBarcodeInput();
        break;
      case "F4":
        incrementLastMarketItem();
        break;
      case "F6":
        if (!canUsePromos) {
          toast.info("Fitur promo tidak tersedia pada paket saat ini");
          return;
        }
        promoSelectRef.current?.focus();
        break;
      case "F7":
        setMethod("cash");
        window.setTimeout(() => cashInputRef.current?.focus(), 0);
        break;
      case "F8":
        if (!canUseQris || !qrisMode.configured) {
          toast.error("QRIS outlet belum dikonfigurasi");
          return;
        }
        setMethod("qris");
        break;
      case "F9":
        if (!paying) void pay();
        break;
      case "F10":
        setPrinterSettingsOpen(true);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (viewMode !== "market" || qrisSale || paidSale || printerSettingsOpen) return undefined;
    const handler = (event) => {
      const shortcut = String(event.key || "").toUpperCase();
      if (!["F2", "F3", "F4", "F6", "F7", "F8", "F9", "F10"].includes(shortcut)) return;
      event.preventDefault();
      runMarketShortcut(shortcut);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const doInquiry = useCallback(async (saleId, silent = false) => {
    try {
      const r = await api.post(`/sales/${saleId}/confirm-payment`);
      const result = r.data || {};
      if (result.status === "paid") {
        const sale = (await api.get(`/sales/${saleId}`)).data;
        setPaidSale(sale);
        setQrisSale(null);
        setInquiryStatus("");
        setInquiryAttempts(0);
        clear();
        await loadProducts();
        toast.success("Pembayaran QRIS sukses");
        return { ...result, paid: true };
      }
      if (!silent) {
        if (result.status === "failed") toast.warning(result.message || "Transaksi provider sudah tidak dapat dibayar");
        else toast.info(result.message || "Belum terbayar. Coba lagi.");
      }
      return result;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Gagal cek status");
      return { status: "error", message: e?.response?.data?.detail || "Inquiry gagal" };
    }
  }, [loadProducts]);

  const manualInquiry = async () => {
    if (!qrisSale) return;
    setManualChecking(true);
    await doInquiry(qrisSale.id, false);
    setManualChecking(false);
  };

  useEffect(() => {
    if (!qrisSale || qrisSale.qris?.provider !== "static") {
      setStaticQrImageUrl("");
      setStaticQrImageLoading(false);
      return undefined;
    }

    let active = true;
    let objectUrl = "";
    const protectedPath = String(
      qrisSale.qris?.qr_url || `/qris-config/outlet/${qrisSale.outlet_id}/static-image`
    ).replace(/^\/api(?=\/|$)/, "");

    setStaticQrImageUrl("");
    setStaticQrImageLoading(true);
    api.get(protectedPath, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setStaticQrImageUrl(objectUrl);
      })
      .catch(() => {
        if (active) toast.error("Gambar QRIS statis tidak dapat dimuat");
      })
      .finally(() => { if (active) setStaticQrImageLoading(false); });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qrisSale]);

  const confirmStaticPayment = async () => {
    if (!qrisSale || qrisSale.qris?.provider !== "static") return;
    const confirmed = window.confirm("Pastikan dana QRIS sudah terlihat pada rekening merchant. Konfirmasi pembayaran sekarang?");
    if (!confirmed) return;

    setManualChecking(true);
    try {
      const response = await api.post(`/sales/${qrisSale.id}/confirm-payment`, { manual_confirm: true });
      if (response.data?.status !== "paid") {
        toast.error(response.data?.message || "Pembayaran belum dapat dikonfirmasi");
        return;
      }
      const sale = (await api.get(`/sales/${qrisSale.id}`)).data;
      setPaidSale(sale);
      setQrisSale(null);
      clear();
      await loadProducts();
      toast.success("Pembayaran QRIS statis dikonfirmasi");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal mengonfirmasi pembayaran");
    } finally {
      setManualChecking(false);
    }
  };

  const qrisExpiryMs = useMemo(() => {
    if (!qrisSale || !["yokke", "midtrans"].includes(qrisSale.qris?.provider)) return 0;
    const explicit = Date.parse(qrisSale.qris?.expires_at || "");
    if (Number.isFinite(explicit)) return explicit;
    const generated = Date.parse(qrisSale.qris?.generated_at || qrisSale.created_at || "");
    return Number.isFinite(generated) ? generated + (5 * 60 * 1000) : Date.now() + (5 * 60 * 1000);
  }, [qrisSale]);

  useEffect(() => {
    if (!qrisExpiryMs) {
      setQrisRemainingSeconds(300);
      return undefined;
    }
    const tick = () => setQrisRemainingSeconds(
      Math.max(0, Math.ceil((qrisExpiryMs - Date.now()) / 1000))
    );
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [qrisExpiryMs]);

  // Poll dynamic QRIS every 5 seconds until the backend-defined 5-minute TTL ends.
  // Timeout/technical errors remain retryable and never turn the sale PAID/FAILED locally.
  useEffect(() => {
    if (!qrisSale || !["yokke", "midtrans"].includes(qrisSale.qris?.provider)) {
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
      setInquiryAttempts(0);
      setInquiryStatus("");
      return undefined;
    }
    if (qrisExpiryMs && Date.now() >= qrisExpiryMs) {
      setInquiryStatus("expired");
      return undefined;
    }
    if (inquiryStatus === "terminal") return undefined;

    setInquiryStatus("polling");
    pollingTimerRef.current = window.setTimeout(async () => {
      const result = await doInquiry(qrisSale.id, true);
      if (result?.paid) return;
      setInquiryAttempts((n) => n + 1);
      if (["failed", "refunded"].includes(String(result?.status || "").toLowerCase())) {
        setInquiryStatus("terminal");
      }
    }, 5000);

    return () => {
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
    };
  }, [qrisSale, inquiryAttempts, inquiryStatus, qrisExpiryMs, doInquiry]);

  const printBrowserReceipt = () => {
    if (!receiptRef.current) return;
    const win = window.open("", "_blank", "width=380,height=600");
    if (!win) {
      toast.error("Popup cetak diblokir browser");
      return;
    }
    const paperWidth = printerSettings.paperWidth === 80 ? 80 : 58;
    const contentWidth = paperWidth === 80 ? 72 : 48;
    const fontSize = paperWidth === 80 ? 11 : 9;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Struk ${paidSale?.invoice_no || ""}</title><style>
      @page { size: ${paperWidth}mm auto; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      body { width: ${paperWidth}mm; font-family: "Courier New", Courier, monospace; font-size: ${fontSize}px; line-height: 1.25; }
      .receipt-paper { width: ${contentWidth}mm; margin: 0 auto; padding: 3mm 0 7mm; }
      .receipt-store { text-align: center; font-size: 15px; line-height: 1.15; font-weight: 700; text-transform: uppercase; }
      .receipt-centre { text-align: center; }
      .receipt-small { font-size: 9px; }
      .receipt-separator { margin: 4px 0; overflow: hidden; white-space: nowrap; letter-spacing: .3px; }
      .receipt-meta, .receipt-row { display: flex; justify-content: space-between; gap: 8px; }
      .receipt-meta > :first-child, .receipt-row > :first-child { min-width: 0; }
      .receipt-meta > :last-child, .receipt-row > :last-child { flex: none; text-align: right; }
      .receipt-item { margin: 4px 0; }
      .receipt-item-name { font-weight: 700; text-transform: uppercase; overflow-wrap: anywhere; }
      .receipt-item-detail { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding-left: 6px; }
      .receipt-total { font-size: 14px; font-weight: 700; margin-top: 2px; }
      .receipt-footer { text-align: center; margin-top: 7px; }
      .receipt-strong { font-weight: 700; }
    </style></head><body>${receiptRef.current.outerHTML}</body></html>`);
    win.document.close(); win.focus();
    setTimeout(() => { win.print(); win.close(); }, 200);
  };

  const updatePrinterSettings = (patch) => {
    const next = savePrinterSettings({ ...printerSettings, ...patch });
    setPrinterSettings(next);
    return next;
  };

  const refreshPrinterStatus = () => {
    const status = getReceiptPrinterStatus();
    setPrinterConnected(status.connected);
    setPrinterLabel(status.label);
    return status;
  };

  const handleConnectPrinter = async () => {
    if (!isWebSerialSupported()) {
      toast.error("Web Serial tidak tersedia. Gunakan Chrome/Edge terbaru dan buka KRAYA melalui HTTPS.");
      return;
    }
    setPrinterBusy(true);
    try {
      const result = await connectReceiptPrinter(printerSettings, { request: true });
      setPrinterConnected(true);
      setPrinterLabel(result.label);
      toast.success(`Printer terhubung: ${result.label}`);
    } catch (error) {
      refreshPrinterStatus();
      if (error?.name !== "NotFoundError") toast.error(error?.message || "Printer tidak dapat dihubungkan");
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleDisconnectPrinter = async () => {
    setPrinterBusy(true);
    try {
      await disconnectReceiptPrinter();
      refreshPrinterStatus();
      toast.success("Koneksi printer diputus");
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleTestPrinter = async () => {
    setPrinterBusy(true);
    try {
      const result = await printEscPosTest(printerSettings);
      setPrinterConnected(true);
      setPrinterLabel(result.label);
      toast.success("Test print berhasil dikirim");
    } catch (error) {
      refreshPrinterStatus();
      if (error?.name !== "NotFoundError") toast.error(error?.message || "Test print gagal");
    } finally {
      setPrinterBusy(false);
    }
  };

  const handlePrintReceipt = async () => {
    if (printerSettings.mode !== "serial") {
      printBrowserReceipt();
      return;
    }
    setPrinterBusy(true);
    try {
      const result = await printEscPosReceipt({
        sale: paidSale,
        outlet: paidSale ? outlets.find((outlet) => outlet.id === paidSale.outlet_id) : null,
        settings: printerSettings,
        paymentLabel: paidSale?.payment_method === "qris"
          ? (paidSale?.qris?.provider === "static" ? "QRIS STATIS" : "QRIS DINAMIS")
          : "TUNAI",
        qrisDetails: getQrisReceiptDetails(paidSale),
      }, { requestIfMissing: true });
      setPrinterConnected(true);
      setPrinterLabel(result.label);
      toast.success("Struk berhasil dikirim ke printer");
    } catch (error) {
      refreshPrinterStatus();
      if (error?.name !== "NotFoundError") toast.error(error?.message || "Struk gagal dicetak");
      setPrinterSettingsOpen(true);
    } finally {
      setPrinterBusy(false);
    }
  };

  const receiptOutlet = paidSale ? outlets.find((o) => o.id === paidSale.outlet_id) : null;
  const receiptQty = paidSale?.items?.reduce((sum, item) => sum + Number(item.qty || 0), 0) || 0;
  const paidTaxableBase = paidSale ? Math.max(0, Number(paidSale.subtotal || 0) - Number(paidSale.discount || 0)) : 0;
  const paidTaxRate = paidSale?.tax_rate || (paidTaxableBase > 0 && Number(paidSale?.tax || 0) > 0
    ? Number(((Number(paidSale.tax) / paidTaxableBase) * 100).toFixed(2))
    : 0);
  const receiptPaymentLabel = paidSale?.payment_method === "qris"
    ? (paidSale?.qris?.provider === "static" ? "QRIS STATIS" : "QRIS DINAMIS")
    : "TUNAI";
  const qrisReceiptDetails = getQrisReceiptDetails(paidSale);

  useEffect(() => {
    if (viewMode !== "market") return undefined;
    const timer = window.setTimeout(() => barcodeInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [viewMode, outletId]);

  useEffect(() => {
    if (printerSettings.mode !== "serial" || !isWebSerialSupported()) return undefined;
    let active = true;
    connectReceiptPrinter(printerSettings, { request: false })
      .then((result) => {
        if (!active) return;
        setPrinterConnected(true);
        setPrinterLabel(result.label);
      })
      .catch(() => {
        if (active) refreshPrinterStatus();
      });
    return () => { active = false; };
    // Reconnect only when the saved connection mode or serial speed changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printerSettings.mode, printerSettings.baudRate]);

  useEffect(() => {
    if (!paidSale || printerSettings.mode !== "serial" || !printerSettings.autoPrint) return;
    const saleKey = String(paidSale.id || paidSale.invoice_no || "");
    if (!saleKey || autoPrintedSaleRef.current === saleKey) return;
    autoPrintedSaleRef.current = saleKey;

    printEscPosReceipt({
      sale: paidSale,
      outlet: receiptOutlet,
      settings: printerSettings,
      paymentLabel: receiptPaymentLabel,
      qrisDetails: qrisReceiptDetails,
    }, { requestIfMissing: false })
      .then((result) => {
        setPrinterConnected(true);
        setPrinterLabel(result.label);
        toast.success("Struk tercetak otomatis");
      })
      .catch(() => {
        refreshPrinterStatus();
        toast.warning("Auto-print tidak berjalan. Hubungkan printer lalu tekan Cetak.");
      });
    // paidSale identifies a completed transaction; the remaining values are snapshots.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidSale]);

  return (
    <div className="h-[calc(100vh-56px)] grid grid-cols-1 lg:grid-cols-[1fr_400px]" data-testid="pos-page">
      {/* Left: products / supermarket scanner */}
      <div className="flex flex-col border-r border-border min-h-0">
        <div className="p-4 border-b border-border space-y-3 bg-white">
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="pos-outlet-select" value={outletId} onChange={(e)=>setOutletId(e.target.value)} className="border border-border px-3 py-2 text-sm">
              {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button
              type="button"
              onClick={() => setPrinterSettingsOpen(true)}
              className={`inline-flex items-center gap-1.5 border px-3 py-2 text-xs font-semibold ${printerSettings.mode === "serial" && printerConnected ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-border bg-white text-zinc-700"}`}
              title="Pengaturan printer struk"
              data-testid="printer-settings-open-btn"
            >
              <Printer className="h-4 w-4" />
              <span className="hidden xl:inline">{printerSettings.mode === "serial" && printerConnected ? "Printer Siap" : "Atur Printer"}</span>
            </button>

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <div className="inline-flex border border-border bg-zinc-50 p-0.5" data-testid="pos-view-mode-switch">
                <button
                  type="button"
                  onClick={() => changeViewMode("touch")}
                  className={`px-3 py-1.5 text-xs font-semibold ${viewMode === "touch" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-white"}`}
                  data-testid="pos-view-touch"
                >
                  TOUCH POS
                </button>
                <button
                  type="button"
                  onClick={() => changeViewMode("market")}
                  className={`px-3 py-1.5 text-xs font-semibold ${viewMode === "market" ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-white"}`}
                  data-testid="pos-view-market"
                >
                  MINIMARKET
                </button>
              </div>
              <label className="inline-flex items-center gap-2 border border-border bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                Default
                <select
                  value={defaultViewMode}
                  onChange={(event) => changeDefaultViewMode(event.target.value)}
                  className="bg-transparent text-xs font-semibold text-zinc-800 outline-none"
                  data-testid="pos-default-view-mode"
                  title="Tampilan default saat halaman kasir dibuka"
                >
                  <option value="touch">Touch POS</option>
                  <option value="market">Minimarket</option>
                </select>
              </label>
            </div>
          </div>

          {viewMode === "touch" ? (
            <>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input data-testid="pos-search-input" value={q} onChange={(e)=>setQ(e.target.value)}
                  className="w-full border border-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  placeholder="Cari nama / SKU / barcode…" />
              </div>
              <div className="flex gap-2 overflow-x-auto">
                <button onClick={() => setCat("all")} className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold border " + (cat==="all"?"bg-zinc-900 text-white border-zinc-900":"border-border")}>Semua</button>
                {categories.map((c) => (
                  <button key={c.id} onClick={() => setCat(c.id)} className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold border whitespace-nowrap " + (cat===c.id?"bg-zinc-900 text-white border-zinc-900":"border-border")}>{c.name}</button>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <label htmlFor="pos-barcode-input" className="overline text-zinc-500">Scan Barcode Barang</label>
              <div className="relative">
                <Barcode className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#002FA7]" />
                <input
                  ref={barcodeInputRef}
                  id="pos-barcode-input"
                  data-testid="pos-barcode-input"
                  value={barcodeValue}
                  onChange={(e) => setBarcodeValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      scanBarcode();
                    }
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full border-2 border-[#002FA7] bg-blue-50/30 pl-11 pr-4 py-3 text-lg font-mono tracking-wide focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="Scan barcode — barang otomatis masuk…"
                />
              </div>
              <div className="text-xs text-zinc-500">Tidak perlu tekan tombol Tambah. Barcode exact-match otomatis masuk ke daftar belanja; suffix Enter dari scanner tetap didukung.</div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {viewMode === "touch" ? (
            <div className="p-4">
              {filtered.length === 0 ? (
                <div className="text-center text-sm text-zinc-500 py-20">Belum ada produk. Tambahkan dari menu Produk.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 grid-borders">
                  {filtered.map((p) => (
                    <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => add(p)} disabled={Number(p.stock || 0) <= 0 || Number(p.return_pending_count || 0) > 0}
                      className="p-4 text-left bg-white hover:bg-zinc-50 transition-colors btn-press">
                      <div className="aspect-square bg-zinc-100 mb-3 flex items-center justify-center overflow-hidden">
                        {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-display font-black text-3xl text-zinc-300">{p.name?.[0]}</span>}
                      </div>
                      <div className="text-sm font-semibold line-clamp-2">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 tabular-nums">{Number(p.return_pending_count || 0) > 0 ? "Karantina retur" : `Stok: ${p.stock ?? 0}`}</div>
                      <div className="font-display font-bold text-base mt-2 tabular-nums">{formatIDR(p.price)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="min-h-full bg-zinc-50">
              <div className="border-b border-border bg-white p-3">
                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="pos-market-shortcuts">
                  {[
                    ["F2", "Cari Produk"],
                    ["F3", "Scan Barcode"],
                    ["F4", "Qty +1"],
                    ["F6", "Promo"],
                    ["F7", "Tunai"],
                    ["F8", "QRIS"],
                    ["F9", "Bayar"],
                    ["F10", "Printer"],
                  ].map(([shortcut, label]) => {
                    const disabled = (shortcut === "F6" && !canUsePromos) || (shortcut === "F8" && (!canUseQris || !qrisMode.configured));
                    return (
                      <button
                        key={shortcut}
                        type="button"
                        onClick={() => runMarketShortcut(shortcut)}
                        disabled={disabled}
                        className="inline-flex items-center gap-1.5 border border-border bg-zinc-50 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:border-zinc-400 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                        title={`Shortcut ${shortcut}: ${label}`}
                      >
                        <kbd className="border border-zinc-300 bg-white px-1 py-0.5 font-mono text-[9px] font-bold text-[#002FA7]">{shortcut}</kbd>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    ref={marketSearchRef}
                    value={marketSearch}
                    onChange={(e) => setMarketSearch(e.target.value)}
                    className="w-full border border-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    placeholder="F2 · Cari produk manual jika barcode rusak / tidak terbaca…"
                    data-testid="pos-market-search"
                  />
                </div>
                {marketSearch && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-border bg-white">
                    {marketSearchResults.length === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-zinc-500">Produk tidak ditemukan.</div>
                    ) : marketSearchResults.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => addFromMarketSearch(product)} disabled={Number(product.stock || 0) <= 0 || Number(product.return_pending_count || 0) > 0}
                        className="grid w-full grid-cols-[1fr_auto] items-center gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-zinc-50"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">{product.name}</div>
                          <div className="text-xs text-zinc-500">{product.barcode || product.sku || "Tanpa barcode"} · {Number(product.return_pending_count || 0) > 0 ? "Karantina retur" : `Stok ${product.stock ?? 0}`}</div>
                        </div>
                        <div className="text-sm font-semibold tabular-nums">{formatIDR(product.price)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse text-sm" data-testid="pos-market-cart-table">
                  <thead className="sticky top-0 z-10 bg-zinc-900 text-white">
                    <tr className="text-left text-[11px] uppercase tracking-wider">
                      <th className="px-3 py-2 font-semibold">Barcode / SKU</th>
                      <th className="px-3 py-2 font-semibold">Produk</th>
                      <th className="px-3 py-2 text-center font-semibold">Qty</th>
                      <th className="px-3 py-2 text-right font-semibold">Harga</th>
                      <th className="px-3 py-2 text-right font-semibold">Total</th>
                      <th className="w-10 px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-20 text-center text-zinc-500">
                          <Barcode className="mx-auto mb-3 h-10 w-10 text-zinc-300" />
                          <div className="font-semibold text-zinc-600">Belum ada barang</div>
                          <div className="mt-1 text-xs">Arahkan scanner ke barcode produk untuk mulai transaksi.</div>
                        </td>
                      </tr>
                    ) : cart.map((item, index) => {
                      const product = cartProductMap.get(item.product_id);
                      return (
                        <tr key={item.product_id || index} className="border-b border-border bg-white hover:bg-blue-50/40">
                          <td className="px-3 py-3 font-mono text-xs text-zinc-500">{product?.barcode || product?.sku || "-"}</td>
                          <td className="px-3 py-3">
                            <div className="font-semibold">{item.name}</div>
                            <div className="mt-0.5 text-xs text-zinc-400">Stok: {product?.stock ?? "-"}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="mx-auto inline-flex items-center border border-border bg-white">
                              <button type="button" onClick={() => dec(index)} className="px-2 py-1.5 hover:bg-zinc-100"><Minus className="h-3 w-3"/></button>
                              <div className="min-w-10 px-2 text-center font-bold tabular-nums">{item.qty}</div>
                              <button type="button" onClick={() => inc(index)} className="px-2 py-1.5 hover:bg-zinc-100"><Plus className="h-3 w-3"/></button>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">{formatIDR(item.price)}</td>
                          <td className="px-3 py-3 text-right font-bold tabular-nums">{formatIDR(item.qty * item.price)}</td>
                          <td className="px-2 py-3 text-center">
                            <button type="button" onClick={() => remove(index)} className="p-1 text-zinc-400 hover:text-rose-600" aria-label={`Hapus ${item.name}`}><X className="h-4 w-4"/></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: cart / transaction summary */}
      <div className="flex flex-col bg-zinc-50 min-h-0">
        <div className="p-4 border-b border-border flex items-center justify-between bg-white">
          <div className="flex items-center gap-2"><ShoppingCart className="h-4 w-4"/><div className="overline">{viewMode === "market" ? `Ringkasan (${cartItemCount} item)` : `Keranjang (${cart.length})`}</div></div>
          {cart.length>0 && <button onClick={clear} data-testid="cart-clear-btn" className="text-xs text-rose-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3"/>Kosongkan</button>}
        </div>
        {viewMode === "touch" ? (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.length===0 && <div className="text-center text-sm text-zinc-500 py-20">Tap produk di kiri untuk menambah</div>}
            {cart.map((c, i) => (
              <div key={i} className="bg-white border border-border p-3" data-testid={`cart-item-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold leading-tight">{c.name}</div>
                  <button onClick={() => remove(i)} className="text-zinc-400 hover:text-rose-600"><X className="h-3.5 w-3.5"/></button>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 tabular-nums">{formatIDR(c.price)} / item</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="inline-flex items-center border border-border">
                    <button onClick={() => dec(i)} className="px-2 py-1.5 hover:bg-zinc-100"><Minus className="h-3 w-3"/></button>
                    <div className="px-3 text-sm font-semibold tabular-nums" data-testid={`cart-item-${i}-qty`}>{c.qty}</div>
                    <button onClick={() => inc(i)} className="px-2 py-1.5 hover:bg-zinc-100"><Plus className="h-3 w-3"/></button>
                  </div>
                  <div className="font-semibold tabular-nums text-sm">{formatIDR(c.qty * c.price)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-border bg-white p-3">
                <div className="overline text-zinc-500">Item</div>
                <div className="mt-1 font-display text-2xl font-black tabular-nums">{cartItemCount}</div>
              </div>
              <div className="border border-border bg-white p-3">
                <div className="overline text-zinc-500">Produk</div>
                <div className="mt-1 font-display text-2xl font-black tabular-nums">{cart.length}</div>
              </div>
            </div>
            {lastScannedProduct && (
              <div className="mt-3 border border-blue-200 bg-blue-50 p-3" data-testid="pos-last-scanned">
                <div className="overline text-[#002FA7]">Terakhir Dipindai</div>
                <div className="mt-1 text-sm font-bold">{lastScannedProduct.name}</div>
                <div className="mt-1 flex justify-between gap-3 text-xs text-zinc-600">
                  <span className="font-mono">{lastScannedProduct.barcode || lastScannedProduct.sku || "-"}</span>
                  <span className="font-semibold tabular-nums">{formatIDR(lastScannedProduct.price)}</span>
                </div>
              </div>
            )}
            <div className="mt-3 text-xs leading-5 text-zinc-500">Daftar barang lengkap tampil di tabel sebelah kiri. Setelah scan berhasil, fokus otomatis kembali ke kolom barcode untuk scan berikutnya.</div>
          </div>
        )}
        {/* Payment */}
        <div className="border-t border-border bg-white p-4 space-y-3">
          {canUsePromos && (
            <div className="space-y-1.5" data-testid="pos-promo-panel">
            <label htmlFor="pos-promo-select" className="overline text-zinc-500">Promo Aktif</label>
            <div className="flex gap-2">
              <select
                ref={promoSelectRef}
                id="pos-promo-select"
                data-testid="pos-promo-select"
                value={promoCode}
                disabled={promosLoading || !outletId}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setAppliedPromo(null);
                }}
                className="min-w-0 flex-1 border border-border px-3 py-2 text-sm bg-white disabled:bg-zinc-100 disabled:text-zinc-400"
              >
                <option value="">{promosLoading ? "Memuat promo..." : "Tanpa promo"}</option>
                {activePromos.map((promo) => {
                  const minimum = Number(promo.min_purchase || 0);
                  const notEligible = subtotal < minimum;
                  const benefit = promo.type === "percent"
                    ? `${Number(promo.value || 0)}%`
                    : formatIDR(promo.value || 0);
                  return (
                    <option key={promo.id || promo.code} value={promo.code} disabled={notEligible}>
                      {promo.code} — {promo.name} ({benefit})
                      {notEligible ? ` · min. ${formatIDR(minimum)}` : ""}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={() => applyPromo()}
                disabled={!promoCode || promoChecking || subtotal <= 0}
                data-testid="pos-promo-apply-btn"
                className="px-4 py-2 text-xs font-semibold border border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {promoChecking ? "CEK..." : "PAKAI"}
              </button>
            </div>
            {!promosLoading && activePromos.length === 0 && (
              <div className="text-xs text-zinc-500">Tidak ada promo aktif untuk outlet ini.</div>
            )}
            </div>
          )}
          {canUsePromos && appliedPromo && (
            <div className="flex items-center justify-between gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
              <span>
                {appliedPromo.promo.name} ({appliedPromo.promo.code}) · Potongan {formatIDR(appliedPromo.discount)}
              </span>
              <button
                type="button"
                onClick={() => { setPromoCode(""); setAppliedPromo(null); }}
                className="font-semibold text-emerald-800 hover:underline"
                data-testid="pos-promo-remove-btn"
              >
                Hapus
              </button>
            </div>
          )}
          {canUseTax && (
            <div className="border border-border bg-zinc-50 p-3" data-testid="pos-tax-panel">
            <div className="flex items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={(e) => setTaxEnabled(e.target.checked)}
                  data-testid="pos-tax-toggle"
                  className="h-4 w-4 accent-[#002FA7]"
                />
                Tambahkan PPN
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={!taxEnabled}
                  data-testid="pos-tax-rate"
                  className="w-20 border border-border bg-white px-2 py-1.5 text-right text-sm tabular-nums disabled:bg-zinc-100 disabled:text-zinc-400"
                  aria-label="Tarif PPN"
                />
                <span className="text-sm text-zinc-600">%</span>
              </div>
            </div>
            {taxEnabled && (
              <div className="mt-2 flex justify-between text-xs text-zinc-500">
                <span>Dasar pengenaan setelah diskon</span>
                <span className="tabular-nums">{formatIDR(taxableBase)}</span>
              </div>
            )}
            </div>
          )}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span className="tabular-nums">{formatIDR(subtotal)}</span></div>
            {discount>0 && <div className="flex justify-between text-zinc-600"><span>Diskon</span><span className="tabular-nums">- {formatIDR(discount)}</span></div>}
            {canUseTax && taxEnabled && tax>0 && <div className="flex justify-between text-zinc-600"><span>PPN ({normalizedTaxRate}%)</span><span className="tabular-nums">{formatIDR(tax)}</span></div>}
            <div className="flex justify-between font-display text-2xl font-black"><span>TOTAL</span><span className="tabular-nums" data-testid="pos-total">{formatIDR(total)}</span></div>
          </div>
          <div className={`grid ${canUseQris ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
            <button data-testid="pos-method-cash" onClick={() => setMethod("cash")} className={"min-h-[56px] rounded-xl border px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] inline-flex items-center justify-center gap-2 text-center transition " + (method==="cash" ? "border-zinc-900 bg-zinc-900 text-white shadow-sm" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50")}>
              <Banknote className="h-4 w-4 shrink-0"/>Tunai
            </button>
            {canUseQris && (
              <button
                data-testid="pos-method-qris"
                onClick={() => qrisMode.configured ? setMethod("qris") : toast.error("QRIS outlet belum dikonfigurasi")}
                disabled={qrisModeLoading || !qrisMode.configured}
                className={"min-h-[56px] rounded-xl border px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] inline-flex items-center justify-center gap-2 text-center whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-40 " + (method==="qris" ? "border-zinc-900 bg-zinc-900 text-white shadow-sm" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50")}
                title={qrisMode.configured ? (qrisMode.mode === "static" ? "QRIS Statis" : "QRIS Dinamis") : "Atur QRIS melalui menu QRIS Outlet"}
              >
                <QrCode className="h-4 w-4 shrink-0"/>{qrisModeLoading ? "Memuat QRIS…" : (qrisMode.mode === "static" ? "QRIS Statis" : "QRIS Dinamis")}
              </button>
            )}
          </div>
          {method==="cash" && (
            <div>
              <label className="overline text-zinc-600 block mb-1.5">Uang Diterima</label>
              <input ref={cashInputRef} data-testid="pos-cash-input" type="number" value={cash} onChange={(e)=>setCash(e.target.value)}
                className="w-full border border-border px-4 py-3 text-lg font-mono-num focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              {Number(cash)>=total && total>0 && <div className="mt-1 text-xs text-zinc-600">Kembalian: <span className="tabular-nums font-semibold" data-testid="pos-change">{formatIDR(change)}</span></div>}
              <div className="grid grid-cols-4 gap-1 mt-2">
                {[total, 50000, 100000, 200000].map((v, i) => (
                  <button key={i} onClick={()=>setCash(String(v))} className="text-xs py-2 border border-border hover:bg-zinc-100">{i===0?"Pas":formatIDR(v).replace("Rp ","")}</button>
                ))}
              </div>
            </div>
          )}
          <button onClick={pay} disabled={paying || cart.length===0} data-testid="pos-pay-btn"
            className="w-full h-14 bg-[#002FA7] text-white font-display text-lg font-bold tracking-tight hover:bg-[#00227A] disabled:opacity-50 btn-press transition-colors">
            {paying ? "Memproses…" : `BAYAR ${formatIDR(total)}`}
          </button>
        </div>
      </div>

      {/* Printer settings dialog */}
      <Dialog open={printerSettingsOpen} onOpenChange={setPrinterSettingsOpen}>
        <DialogContent className="rounded-none border-border max-w-lg" data-testid="printer-settings-dialog">
          <DialogHeader>
            <DialogTitle>Pengaturan Printer Struk</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="overline text-zinc-500">Metode Cetak</label>
              <select
                value={printerSettings.mode}
                onChange={(event) => updatePrinterSettings({ mode: event.target.value })}
                className="mt-1 w-full border border-border bg-white px-3 py-2 text-sm"
                data-testid="printer-mode-select"
              >
                <option value="serial">Bluetooth / USB langsung (ESC/POS)</option>
                <option value="browser">Dialog print browser / printer sistem</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-zinc-500">Lebar Kertas</label>
                <select
                  value={printerSettings.paperWidth}
                  onChange={(event) => updatePrinterSettings({ paperWidth: Number(event.target.value) })}
                  className="mt-1 w-full border border-border bg-white px-3 py-2 text-sm"
                >
                  <option value={58}>58 mm (RPP02N)</option>
                  <option value={80}>80 mm</option>
                </select>
              </div>
              <div>
                <label className="overline text-zinc-500">Baud Rate</label>
                <select
                  value={printerSettings.baudRate}
                  disabled={printerSettings.mode !== "serial"}
                  onChange={(event) => updatePrinterSettings({ baudRate: Number(event.target.value) })}
                  className="mt-1 w-full border border-border bg-white px-3 py-2 text-sm disabled:bg-zinc-100"
                >
                  {[9600, 19200, 38400, 57600, 115200].map((rate) => <option key={rate} value={rate}>{rate}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 border border-border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={printerSettings.autoPrint}
                  disabled={printerSettings.mode !== "serial"}
                  onChange={(event) => updatePrinterSettings({ autoPrint: event.target.checked })}
                />
                Cetak otomatis setelah transaksi
              </label>
              <label className="flex items-center gap-2 border border-border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={printerSettings.autoCut}
                  disabled={printerSettings.mode !== "serial"}
                  onChange={(event) => updatePrinterSettings({ autoCut: event.target.checked })}
                />
                Auto cut (bukan RPP02N)
              </label>
            </div>

            {printerSettings.mode === "serial" ? (
              <>
                <div className={`border p-3 text-sm ${printerConnected ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
                  <div className="flex items-center gap-2 font-semibold">
                    <Bluetooth className="h-4 w-4" />
                    {printerConnected ? "Printer terhubung" : "Printer belum terhubung"}
                  </div>
                  <div className="mt-1 text-xs">{printerLabel}</div>
                </div>
                {!isWebSerialSupported() && (
                  <div className="border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800">
                    Browser ini tidak menyediakan Web Serial. Gunakan Google Chrome/Edge terbaru. Android memerlukan Chrome 138 atau lebih baru.
                  </div>
                )}
                <div className="border border-blue-200 bg-blue-50 p-3 text-xs leading-5 text-blue-900">
                  Nyalakan RPP02N, pairing dari pengaturan Bluetooth perangkat menggunakan PIN <b>0000</b>, lalu klik Hubungkan. Saat daftar port muncul, pilih RPP02N atau Bluetooth Serial Port.
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <button type="button" onClick={handleConnectPrinter} disabled={printerBusy || !isWebSerialSupported()} className="inline-flex items-center justify-center gap-1.5 border border-zinc-900 px-3 py-2 text-sm font-semibold disabled:opacity-40">
                    <Bluetooth className="h-4 w-4" />Hubungkan
                  </button>
                  <button type="button" onClick={handleTestPrinter} disabled={printerBusy || !isWebSerialSupported()} className="inline-flex items-center justify-center gap-1.5 bg-zinc-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">
                    <TestTube2 className="h-4 w-4" />Test Print
                  </button>
                  <button type="button" onClick={handleDisconnectPrinter} disabled={printerBusy || !printerConnected} className="col-span-2 inline-flex items-center justify-center gap-1.5 border border-rose-300 px-3 py-2 text-sm font-semibold text-rose-700 disabled:opacity-40 sm:col-span-1">
                    <Unplug className="h-4 w-4" />Putus
                  </button>
                </div>
              </>
            ) : (
              <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs leading-5 text-zinc-700">
                Mode ini membuka dialog print sistem. Printer Bluetooth harus sudah ditambahkan sebagai printer pada Windows/Android Print Service; status pairing Bluetooth saja belum cukup.
              </div>
            )}

            <button type="button" onClick={() => setPrinterSettingsOpen(false)} className="w-full border border-border py-2.5 text-sm font-semibold">Selesai</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QRIS dialog */}
      <Dialog open={!!qrisSale} onOpenChange={(o) => !o && setQrisSale(null)}>
        <DialogContent className="rounded-none border-border" data-testid="qris-dialog">
          <DialogHeader>
            <DialogTitle>{qrisSale?.qris?.provider === "static" ? "Bayar dengan QRIS Statis" : "Bayar dengan QRIS Dinamis"}</DialogTitle>
          </DialogHeader>
          {qrisSale && (
            <div className="space-y-4 text-center">
              <div className="overline text-zinc-500">{qrisSale.invoice_no} · {qrisSale.qris?.provider === "static" ? "QRIS STATIS" : "QRIS DINAMIS"}</div>
              <div className="font-display font-black text-4xl tabular-nums">{formatIDR(qrisSale.total)}</div>
              {qrisSale.qris?.provider === "static" ? (
                staticQrImageUrl ? (
                  <img src={staticQrImageUrl} alt="QRIS Statis" className="mx-auto w-64 h-64 object-contain bg-white border border-border p-2" />
                ) : (
                  <div className="mx-auto w-64 h-64 border border-border bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
                    {staticQrImageLoading ? "Memuat QRIS statis…" : "Gambar QRIS statis tidak tersedia"}
                  </div>
                )
              ) : qrisSale.qris?.qr_url ? (
                <img src={qrisSale.qris.qr_url} alt="QRIS" className="mx-auto w-64 h-64" />
              ) : (
                <div className="mx-auto inline-block p-4 bg-white border border-border">
                  <QRCodeSVG value={qrisSale.qris?.qr_string || qrisSale.id} size={240} />
                </div>
              )}
              {["yokke", "midtrans"].includes(qrisSale.qris?.provider) ? (
                <>
                  <p className="text-sm text-zinc-500">Pindai QR dengan aplikasi e-wallet/m-banking. Sistem otomatis melakukan inquiry setiap 5 detik sampai masa berlaku QR 5 menit berakhir.</p>
                  <div className="border border-border p-3 bg-zinc-50 text-left">
                    <div className="flex items-center justify-between">
                      <div className="overline text-zinc-500">Status Pembayaran QRIS</div>
                      <div className="text-xs font-mono tabular-nums">{String(Math.floor(qrisRemainingSeconds / 60)).padStart(2, "0")}:{String(qrisRemainingSeconds % 60).padStart(2, "0")}</div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      {inquiryStatus === "polling" && <><RefreshCw className="h-3.5 w-3.5 animate-spin text-[#002FA7]"/><span>Mengecek pembayaran…</span></>}
                      {inquiryStatus === "expired" && <span className="text-amber-700">Masa berlaku QR 5 menit selesai. Gunakan inquiry manual untuk memastikan tidak ada pembayaran terlambat.</span>}
                      {inquiryStatus === "terminal" && <span className="text-rose-700">Provider menyatakan transaksi sudah tidak dapat dibayar.</span>}
                      {inquiryStatus === "" && <span className="text-zinc-500">Menunggu pembayaran…</span>}
                    </div>
                    <div className="h-1.5 bg-zinc-200 mt-2 overflow-hidden">
                      <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${Math.max(0, Math.min(100, ((300 - qrisRemainingSeconds) / 300) * 100))}%` }} />
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-1">Inquiry otomatis: {inquiryAttempts}x</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setQrisSale(null); toast.info("QRIS ditutup. Transaksi tetap PENDING dan dapat dipantau di Rekonsiliasi."); }} className="py-3 text-sm border border-border" data-testid="qris-cancel-btn">Tutup</button>
                    <button onClick={manualInquiry} disabled={manualChecking} data-testid="qris-manual-inquiry-btn"
                      className="py-3 text-sm bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4"/>{manualChecking ? "Mengecek…" : "Inquiry Manual"}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-400">Inquiry manual dapat digunakan kapan saja bila customer sudah menyelesaikan pembayaran.</p>
                  <p className="text-xs text-amber-700">Menutup dialog tidak membatalkan QRIS yang masih aktif. Pembayaran terlambat dapat ditangani melalui Rekonsiliasi.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-zinc-500">Pindai QRIS statis outlet. Sistem tidak melakukan inquiry otomatis.</p>
                  <div className="border border-amber-300 bg-amber-50 p-3 text-left text-xs text-amber-900">
                    Periksa mutasi atau notifikasi rekening merchant sebelum menekan konfirmasi. Konfirmasi akan mengubah transaksi internal menjadi PAID dan mengurangi stok.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setQrisSale(null); toast.info("Transaksi QRIS statis tetap PENDING sampai dikonfirmasi kasir."); }} className="py-3 text-sm border border-border" data-testid="qris-cancel-btn">Tutup</button>
                    <button onClick={confirmStaticPayment} disabled={manualChecking} data-testid="qris-static-confirm-btn"
                      className="py-3 text-sm bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4"/>{manualChecking ? "Memproses…" : "Konfirmasi Pembayaran"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!paidSale} onOpenChange={(o) => !o && setPaidSale(null)}>
        <DialogContent className="rounded-none border-border max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle>Struk Pembayaran</DialogTitle>
          </DialogHeader>
          {paidSale && (
            <>
              <div ref={receiptRef} className="receipt-paper font-mono text-[11px] leading-tight bg-white text-black p-4 border border-zinc-200">
                <div className="receipt-store text-center font-bold text-base uppercase leading-tight">
                  {receiptOutlet?.name || "OUTLET"}
                </div>
                {receiptOutlet?.address && <div className="receipt-centre text-center mt-1">{receiptOutlet.address}</div>}
                {receiptOutlet?.phone && <div className="receipt-centre text-center">Telp. {receiptOutlet.phone}</div>}
                <div className="receipt-centre receipt-small text-center text-[10px] mt-1">BUKTI PEMBAYARAN</div>

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">========================================</div>
                <div className="receipt-meta flex justify-between gap-3"><span>No. Struk</span><span className="text-right">{paidSale.invoice_no}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Tanggal</span><span className="text-right">{new Date(paidSale.created_at).toLocaleDateString("id-ID")}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Jam</span><span className="text-right">{new Date(paidSale.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Kasir</span><span className="text-right uppercase">{paidSale.cashier_name || "-"}</span></div>
                <div className="receipt-meta flex justify-between gap-3"><span>Bayar</span><span className="text-right">{receiptPaymentLabel}</span></div>
                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>

                {paidSale.items.map((it, i) => (
                  <div key={i} className="receipt-item my-1">
                    <div className="receipt-item-name font-bold uppercase break-words">{it.name}</div>
                    <div className="receipt-item-detail grid grid-cols-[1fr_auto] gap-3 pl-2">
                      <span>{it.qty} x {receiptNumber(it.price)}</span>
                      <span className="text-right">{receiptNumber(it.subtotal)}</span>
                    </div>
                  </div>
                ))}

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>
                <div className="receipt-row flex justify-between gap-3"><span>Subtotal</span><span>{receiptNumber(paidSale.subtotal)}</span></div>
                {Number(paidSale.discount || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3"><span>Diskon / Hemat</span><span>-{receiptNumber(paidSale.discount)}</span></div>
                )}
                {Number(paidSale.tax || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3">
                    <span>PPN{paidTaxRate ? ` (${paidTaxRate}%)` : ""}</span>
                    <span>{receiptNumber(paidSale.tax)}</span>
                  </div>
                )}
                <div className="receipt-row receipt-total flex justify-between gap-3 font-bold text-sm mt-1">
                  <span>TOTAL</span><span>Rp {receiptNumber(paidSale.total)}</span>
                </div>
                <div className="receipt-row flex justify-between gap-3"><span>{receiptPaymentLabel}</span><span>{receiptNumber(paidSale.cash_received || paidSale.total)}</span></div>
                {Number(paidSale.change || 0) > 0 && (
                  <div className="receipt-row flex justify-between gap-3"><span>Kembalian</span><span>{receiptNumber(paidSale.change)}</span></div>
                )}

                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">----------------------------------------</div>
                <div className="receipt-row flex justify-between gap-3"><span>Jumlah item</span><span>{receiptQty}</span></div>
                {paidSale.payment_method === "qris" && (
                  <>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Ref. QRIS</span>
                      <span className="text-right">{paidSale.qris?.reference_no || "-"}</span>
                    </div>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Nama Customer</span>
                      <span className="text-right uppercase break-words">{qrisReceiptDetails.customerName || "-"}</span>
                    </div>
                    <div className="receipt-meta receipt-small flex justify-between gap-3 text-[10px]">
                      <span>Approval Code</span>
                      <span className="text-right">{qrisReceiptDetails.approvalCode || "-"}</span>
                    </div>
                  </>
                )}
                <div className="receipt-separator my-1 overflow-hidden whitespace-nowrap">========================================</div>
                <div className="receipt-footer text-center mt-2">
                  <div className="receipt-strong font-bold">TERIMA KASIH</div>
                  <div className="receipt-small text-[10px] mt-1">Simpan struk ini sebagai bukti pembayaran.</div>
                  <div className="receipt-small text-[10px]">Barang yang sudah dibeli mengikuti kebijakan retur outlet.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={() => setPaidSale(null)} className="py-3 text-sm border border-border" data-testid="receipt-close-btn">Tutup</button>
                <button onClick={handlePrintReceipt} disabled={printerBusy} data-testid="receipt-print-btn" className="py-3 text-sm bg-zinc-900 text-white font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50"><Printer className="h-4 w-4"/>{printerBusy ? "Memproses..." : "Cetak"}</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

